// onboardingTour.js — THE QUICK TOUR of the new-account welcome
// (functions/onboarding.js; Nika 2026-10-18 "allow onboarding assistant, tour
// and skipping").
//
// Six stops over the REAL chrome, in reading order — the apps in the navbar,
// search, messages · calls · notifications, the account menu, the App strip,
// the assistant's composer — then a closing card. A stop names its target by
// DOMQL key path from the app root (`el.lookdown`, Rule 40 — never a DOM
// query) and is skipped when that element is not on screen: a phone layout, a
// hidden App strip, a module that hides the composer each simply drop their
// stop, so the tour never points at nothing.
//
// STATE: `root._onboardingTour` = { at, ids, rect, total } while it runs, null
// otherwise — `ids` the stops that resolved at the start, `at` the one shown
// (`ids.length` = the closing card), `rect` its viewport box (read from the
// node, Rule 39). components/OnboardingTour.js renders the dimmed layer with
// its window over `rect` and the card beside it; the card's placement is CSS
// over custom properties (onboardingTourVars), so no card is ever measured.
//
// Skip at any stop: "Skip tour", Escape, Close. Arrow keys walk the stops.
//
// FRANK: every export is self-contained (FA204); siblings through `this.call`.

// The stops, in order. A target is a key path from the app root, or
// `{ path, text, clip }` when that element needs its own line (`text`) or
// scrolls inside a row that shows only part of it (`clip`, the row's key path —
// the Dock's capsule outgrows its row on a phone); the first target that
// resolves to a visible element wins (alternatives — the Dock or the Rail, the
// desktop header or the phone dock, whose Apps / Menu / Assistant buttons stand
// in for the header on a phone: PhoneNavigation).
export function onboardingTourStops() {
  return [
    {
      id: 'apps',
      paths: [
        ['AppHeader', 'HeaderTabs'],
        {
          path: ['PhoneNavigation', 'Dock', 'PhoneNavButton_apps'],
          text: 'workspace.onboarding.tour.appsTextPhone'
        }
      ],
      title: 'workspace.onboarding.tour.appsTitle',
      text: 'workspace.onboarding.tour.appsText'
    },
    {
      id: 'search',
      paths: [
        ['AppHeader', 'SearchBtn'],
        ['AppHeader', 'HeaderSearch']
      ],
      title: 'workspace.onboarding.tour.searchTitle',
      text: 'workspace.onboarding.tour.searchText'
    },
    {
      id: 'inbox',
      paths: [['AppHeader', 'HeaderActions']],
      title: 'workspace.onboarding.tour.inboxTitle',
      text: 'workspace.onboarding.tour.inboxText'
    },
    {
      id: 'account',
      paths: [
        ['AppHeader', 'MeWrapper'],
        {
          path: ['PhoneNavigation', 'Dock', 'PhoneNavButton_menu'],
          text: 'workspace.onboarding.tour.accountTextPhone'
        }
      ],
      title: 'workspace.onboarding.tour.accountTitle',
      text: 'workspace.onboarding.tour.accountText'
    },
    {
      id: 'strip',
      paths: [
        { path: ['HomeAppStrip', 'DockCapsule'], clip: ['HomeAppStrip'] },
        ['HomeAppRail', 'RailCapsule']
      ],
      title: 'workspace.onboarding.tour.stripTitle',
      text: 'workspace.onboarding.tour.stripText'
    },
    {
      id: 'composer',
      paths: [
        ['CanvasPromptTextarea'],
        {
          path: ['PhoneNavigation', 'Dock', 'PhoneNavButton_ai'],
          text: 'workspace.onboarding.tour.composerTextPhone'
        }
      ],
      title: 'workspace.onboarding.tour.composerTitle',
      text: 'workspace.onboarding.tour.composerText'
    }
  ]
}

// The viewport box of a stop's target — the part of it that shows — or null
// when it is not on screen. `this` is any shell element; the walk starts at the
// app root.
export function onboardingTourRect(stop) {
  const el = this
  const app = (el && typeof el.getRoot === 'function' && el.getRoot()) || el
  if (!app || !stop || !Array.isArray(stop.paths)) return null
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0
  // A key path from the app root to its element (the DOMQL tree, Rule 40).
  const find = (path) => {
    let node = app
    for (const key of path) {
      node = node && typeof node.lookdown === 'function' ? node.lookdown(key) : null
      if (!node) return null
    }
    return node
  }
  for (const target of stop.paths) {
    const path = Array.isArray(target) ? target : target && target.path
    if (!Array.isArray(path)) continue
    const node = find(path)
    const dom = node && node.node
    if (!dom || typeof dom.getBoundingClientRect !== 'function') continue
    const r = dom.getBoundingClientRect()
    if (!r || r.width < 1 || r.height < 1) continue
    // Off screen (a scrolled-away strip) or under a hidden ancestor.
    if (r.bottom <= 0 || r.right <= 0 || (vw && r.left >= vw) || (vh && r.top >= vh)) continue
    let hidden = false
    try {
      const cs = typeof getComputedStyle === 'function' ? getComputedStyle(dom) : null
      hidden = !!cs && (cs.visibility === 'hidden' || cs.display === 'none' || cs.opacity === '0')
    } catch (_) {
      hidden = false
    }
    if (hidden) continue
    // The part that shows: cut to the row it scrolls in (`clip`), then to the
    // screen — the ring never runs off an edge.
    let left = r.left
    let top = r.top
    let right = r.right
    let bottom = r.bottom
    const row = !Array.isArray(target) && Array.isArray(target.clip) ? find(target.clip) : null
    const cr =
      row && row.node && typeof row.node.getBoundingClientRect === 'function'
        ? row.node.getBoundingClientRect()
        : null
    if (cr && cr.width >= 1 && cr.height >= 1) {
      left = Math.max(left, cr.left)
      top = Math.max(top, cr.top)
      right = Math.min(right, cr.right)
      bottom = Math.min(bottom, cr.bottom)
    }
    if (vw) {
      left = Math.max(left, 0)
      right = Math.min(right, vw)
    }
    if (vh) {
      top = Math.max(top, 0)
      bottom = Math.min(bottom, vh)
    }
    if (right - left < 1 || bottom - top < 1) continue
    const box = {
      x: Math.round(left),
      y: Math.round(top),
      w: Math.round(right - left),
      h: Math.round(bottom - top)
    }
    // The room to the nearest screen edge: the layer's ring gap never exceeds
    // it, so a ring round a target near an edge stays whole on screen.
    if (vw && vh) {
      box.room = Math.max(0, Math.floor(Math.min(left, top, vw - right, vh - bottom)))
    }
    // The line of THIS target, when it has its own (the phone dock's).
    if (!Array.isArray(target) && target.text) box.text = target.text
    return box
  }
  return null
}

// Start the tour: resolve which stops are on screen now, show the first.
// With no stop on screen the closing card alone is shown — the tour still
// ends where it promised.
export function onboardingTourStart() {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const stops = el.call('onboardingTourStops') || []
  const ids = []
  let rect = null
  for (const stop of stops) {
    const r = el.call('onboardingTourRect', stop)
    if (!r) continue
    ids.push(stop.id)
    if (!rect) rect = r
  }
  root.update(
    {
      _onboardingTour: { at: 0, ids, rect, total: ids.length },
      // the tour is the top layer: the welcome and edit mode step aside
      _onboardingOpen: null,
      _homeCustomizeMode: false,
      _homeCustomizeDrawerTucked: false
    },
    { preventFetch: true }
  )
  return true
}

// Move by `delta` stops (+1 next, -1 back). Past the last stop is the closing
// card; a stop whose target left the screen meanwhile is stepped over.
export function onboardingTourGo(delta) {
  const el = this
  const root = el && el.state && el.state.root
  const tour = root && root._onboardingTour
  if (!tour || typeof root.update !== 'function') return false
  const ids = Array.isArray(tour.ids) ? tour.ids : []
  const stops = el.call('onboardingTourStops') || []
  const step = delta < 0 ? -1 : 1
  let at = tour.at + step
  while (at >= 0 && at < ids.length) {
    const stop = stops.find((s) => s.id === ids[at])
    const rect = stop ? el.call('onboardingTourRect', stop) : null
    if (rect) {
      root.update({ _onboardingTour: { ...tour, at, rect } }, { preventFetch: true })
      return true
    }
    at += step
  }
  if (at < 0) return false
  root.update({ _onboardingTour: { ...tour, at: ids.length, rect: null } }, { preventFetch: true })
  return true
}

// Re-read the current stop's box (a resize, a scroll, the layout settling).
// Writes only when the box moved.
export function onboardingTourMeasure() {
  const el = this
  const root = el && el.state && el.state.root
  const tour = root && root._onboardingTour
  if (!tour || typeof root.update !== 'function') return false
  const ids = Array.isArray(tour.ids) ? tour.ids : []
  if (tour.at >= ids.length) return false
  const stops = el.call('onboardingTourStops') || []
  const stop = stops.find((s) => s.id === ids[tour.at])
  const rect = stop ? el.call('onboardingTourRect', stop) : null
  const prev = tour.rect
  if (
    rect &&
    prev &&
    rect.x === prev.x &&
    rect.y === prev.y &&
    rect.w === prev.w &&
    rect.h === prev.h &&
    rect.room === prev.room
  ) {
    return false
  }
  if (!rect) return el.call('onboardingTourGo', 1)
  root.update({ _onboardingTour: { ...tour, rect } }, { preventFetch: true })
  return true
}

// End the tour (Done, Skip tour, Escape, Close).
export function onboardingTourEnd() {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function' || !root._onboardingTour) return false
  root.update({ _onboardingTour: null }, { preventFetch: true })
  return true
}

// The stop on screen: { stop, text, index, total, last } — `stop` null on
// the closing card, `text` the line of the target that resolved. Read by the
// card's texts and buttons.
export function onboardingTourCurrent(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const tour = r && r._onboardingTour
  if (!tour) return null
  const ids = Array.isArray(tour.ids) ? tour.ids : []
  const stops = el.call('onboardingTourStops') || []
  const stop = tour.at < ids.length ? stops.find((s) => s.id === ids[tour.at]) || null : null
  const text = (tour.rect && tour.rect.text) || (stop && stop.text) || ''
  return { stop, text, index: tour.at, total: ids.length, last: tour.at >= ids.length }
}

// WHERE THE CARD GOES (pure): the side of the target with more room — below a
// target in the upper half of the viewport, above one in the lower half — or
// the centre for the closing card. The card's horizontal clamp is CSS (the
// layer's custom properties + the card's own `clamp()`), so nothing is
// measured here.
export function onboardingTourSide(rect, viewportHeight) {
  if (!rect) return 'center'
  const vh =
    typeof viewportHeight === 'number' && viewportHeight > 0
      ? viewportHeight
      : typeof window !== 'undefined'
        ? window.innerHeight
        : 0
  if (!vh) return 'below'
  const centre = rect.y + rect.h / 2
  return centre <= vh / 2 ? 'below' : 'above'
}

// The layer's custom properties for the window and the card: the target box
// in px (viewport coordinates — the layer is fixed at the viewport's origin),
// and its room to the nearest screen edge when it was measured.
export function onboardingTourVars(rect) {
  if (!rect) return {}
  const vars = {
    '--tour-x': rect.x + 'px',
    '--tour-y': rect.y + 'px',
    '--tour-w': rect.w + 'px',
    '--tour-h': rect.h + 'px',
    '--tour-cx': Math.round(rect.x + rect.w / 2) + 'px',
    '--tour-bottom': rect.y + rect.h + 'px'
  }
  if (typeof rect.room === 'number') vars['--tour-room'] = rect.room + 'px'
  return vars
}
